import { Box, Grid, Tooltip, Typography } from "@mui/material";
import * as yup from "yup";
import { PostUser, User } from "../types";
// import { EditIconButton } from "../../../main/components/EditIconButton";
import { useState, useEffect } from "react";
// import { EditTextInfoModal } from "../../../main/components/EditTextInfoModal";
import { usersApi } from "../../../services/api";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";
import ConfirmationDialogUser from "./ConfirmationDialogUser";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import TextInput from "../../../main/components/TextInput";
import BaseButton from "../../../main/components/BaseButton";
import { emailRegex, phoneRegex, validateCPF } from "../../../main/utils/utils";
import CpfInput from "../../../main/components/CpfInput";
import PhoneNumberInput from "../../../main/components/PhoneNumberInput";

const schema = yup.object().shape({
  email: yup
    .string()
    .matches(emailRegex, "Formato de email inválido")
    .required("Email do usuário é obrigatório"),
  phone: yup
    .string()
    .matches(phoneRegex, "Formato de Telefone inválido")
    .nullable(),
  cpf: yup
    .string()
    .test("isCpfValid", "O CPF informado é inválido", (v) =>
      !!v ? validateCPF(v) : true
    )
    .nullable(),
});

export type EditUserFormType = yup.InferType<typeof schema>;

export default function UserDetailsInfos(props: {
  user: User | PostUser;
  onMount: () => void;
}) {
  const { user, onMount } = props;
  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
  });
  const [isCancelConfirmationOpen, setCancelConfirmationOpen] =
    useState<boolean>(false);
  const [isSaveConfirmationOpen, setSaveConfirmationOpen] =
    useState<boolean>(false);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const formData = watch();
  useEffect(() => {
    if (!user) {
      setValue("email", "");
      setValue("cpf", "");
      setValue("phone", "");
      return;
    }
    setValue("email", user.email ?? "");
    setValue("cpf", user.cpf ?? "");
    setValue("phone", user.phone ?? "");
  }, []);

  const onSubmit = async () => {
    setSaveConfirmationOpen(true);
  };

  const handleConfirmCancel = () => {
    if (isEditing) {
      //Limpa os erros ao cancelar uma edição
      clearErrors();
      // Fechar o modo de edição
      setValue("email", user.email ?? "");
      setValue("cpf", user.cpf ?? "");
      setValue("phone", user.phone ?? "");
      setIsEditing(false);
      setCancelConfirmationOpen(false);
    } else {
      setSaveConfirmationOpen(false);
    }
  };

  const canConfirmEdit =
    user.email != formData?.email ||
    user.phone != formData?.phone ||
    user.cpf != formData?.cpf;

  const handleConfirmSave = async () => {
    const userId = user && "id" in user ? user.id : "";

    try {
      await usersApi.patchUserInfos(
        userId,
        user.email === formData?.email ? null : formData?.email || null,
        user.phone === formData?.phone ? null : formData?.phone || null,
        user.cpf === formData?.cpf ? null : formData?.cpf || null
      );
      //Limpar os erros ao salvar com sucesso
      clearErrors();
      // Exibir modal de sucesso
      setOpenSuccessModal(true);

      // Fechar o modo de edição
      setIsEditing(false);

      // Fechar o modal de confirmação
      setSaveConfirmationOpen(false);
    } catch (error) {
      // Exibir modal de erro caso a validação ou as chamadas da API falhem
      console.error("Erro interno no servidor:", error);
      setOpenErrorModal(true);
    }
  };

  return (
    <Grid
      xs={12}
      sm={6}
      component={"form"}
      item
      sx={{ padding: "none" }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid
        container
        sx={{
          flexDirection: "column",
          padding: "20px",
          display: "flex",
          justifyContent: "space-evenly",
          backgroundColor: "rgba(217, 217, 217, 0.1)",
          borderRadius: 2,
          rowGap: "30px ",
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
          }}
          textAlign={"start"}
        >
          <Typography>Informações do usuário</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {isEditing ? (
              <>
                <Tooltip
                  title={canConfirmEdit ? "" : "Nenhuma alteração identificada"}
                >
                  <Box>
                    <PrimaryBaseButton
                      disabled={!canConfirmEdit}
                      title="Salvar"
                      type="submit"
                      sx={{ height: "30px", marginRight: "8px" }}
                    />
                  </Box>
                </Tooltip>
                <DangerBaseButton
                  title={canConfirmEdit ? "Descartar" : "Cancelar"}
                  onClick={() => {
                    canConfirmEdit
                      ? setCancelConfirmationOpen(true)
                      : setIsEditing(false);
                  }}
                  sx={{
                    height: "30px",
                  }}
                />
              </>
            ) : (
              <BaseButton
                title="Editar"
                onClick={() => setIsEditing(!isEditing)}
                sx={{ height: "30px", width: "90px" }}
              />
            )}
          </Box>
        </Grid>
        <Grid
          container
          spacing={2}
          sx={{
            rowGap: "10px",
            paddingTop: "30px",
            paddingBottom: "20px",
          }}
        >
          <Grid item xs={12}>
            <TextInput
              disabled={!isEditing}
              label="Email"
              control={control}
              errors={errors}
              fieldName={"email"}
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CpfInput
              disabled={!isEditing}
              label="CPF"
              control={control}
              errors={errors}
              fieldName={"cpf"}
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneNumberInput
              disabled={!isEditing}
              label="Telefone"
              control={control}
              errors={errors}
              fieldName={"phone"}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>
      </Grid>
      <ConfirmationDialogUser
        open={isCancelConfirmationOpen}
        title="Descartar alteração"
        message="Deseja descartar e cancelar as alterações?"
        onConfirm={handleConfirmCancel}
        onCancel={() => setCancelConfirmationOpen(false)}
        onClose={() => setCancelConfirmationOpen(false)}
      />
      <ConfirmationModal
        open={isSaveConfirmationOpen}
        setOpen={() => setSaveConfirmationOpen(false)}
        title="Editar detalhes"
        contentText="Deseja confirmar a edição dos detalhes ?"
        onConfirm={handleConfirmSave}
      />

      <ActionAlert
        message="Os detalhes foram atualizados com sucesso!"
        severity="success"
        open={openSuccessModal}
        setOpen={() => setOpenSuccessModal(false)}
        onClose={onMount}
      />
      <ActionAlert
        message="Ocorreu um erro no modo de edição! Tente novamente mais tarde."
        severity="error"
        open={openErrorModal}
        setOpen={setOpenErrorModal}
      />
    </Grid>
  );
}
